/**
 * useSiteContent Hook
 * Fetches and manages site content dynamically.
 * Uses a global in-memory cache and request deduplication to avoid 429s when
 * multiple components request the same content key (e.g. home_banner, brand_story).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getImageUrl } from '../utils/imageUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// In-memory cache: key -> { data, timestamp }. TTL 5 min to reduce API calls and avoid 429.
const CACHE_TTL_MS = 5 * 60 * 1000;
const contentCache = new Map();
const inFlight = new Map();

function getCached(key) {
  const normalizedKey = key.trim().toLowerCase();
  const entry = contentCache.get(normalizedKey);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    contentCache.delete(normalizedKey);
    return null;
  }
  return entry.data;
}

function setCached(key, data) {
  const normalizedKey = key.trim().toLowerCase();
  contentCache.set(normalizedKey, { data, timestamp: Date.now() });
}

/**
 * Shared fetch for a content key. Deduplicates in-flight requests and uses cache.
 */
async function fetchContentForKey(normalizedKey, signal, fallback) {
  const cached = getCached(normalizedKey);
  if (cached !== null) return cached;

  const existing = inFlight.get(normalizedKey);
  if (existing) {
    try {
      return await existing;
    } catch {
      return fallback;
    }
  }

  const promise = (async () => {
    try {
      const response = await fetch(`${API_URL}/content/${normalizedKey}`, {
        signal,
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 404) return fallback;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (!data.success || !data.data) return fallback;

      const contentData = {
        ...data.data,
        updatedAt: data.data.updatedAt
          ? (typeof data.data.updatedAt === 'number'
            ? data.data.updatedAt
            : new Date(data.data.updatedAt).getTime())
          : Date.now(),
      };
      setCached(normalizedKey, contentData);
      return contentData;
    } finally {
      inFlight.delete(normalizedKey);
    }
  })();

  inFlight.set(normalizedKey, promise);
  return promise;
}

/**
 * Hook to fetch site content by key
 * @param {string} key - Content key (e.g., 'home_banner', 'brand_story')
 * @param {object} options - Options
 * @param {boolean} options.enabled - Whether to fetch (default: true)
 * @param {object} options.fallback - Fallback content object
 * @returns {object} { content, loading, error, refetch }
 */
export function useSiteContent(key, options = {}) {
  const { enabled = true, fallback = null } = options;
  const normalizedKey = key?.trim().toLowerCase() ?? '';
  const [content, setContent] = useState(() => (enabled && normalizedKey ? getCached(normalizedKey) : null) ?? fallback);
  const [loading, setLoading] = useState(enabled && normalizedKey && !getCached(normalizedKey));
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const latestFallbackRef = useRef(fallback);

  // Keep a ref to the latest fallback so we don't recreate callbacks
  // (and trigger effects) every time a component passes a new object literal.
  useEffect(() => {
    latestFallbackRef.current = fallback;
  }, [fallback]);

  const fetchContent = useCallback(() => {
    if (!enabled || !normalizedKey) {
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    fetchContentForKey(normalizedKey, signal, latestFallbackRef.current)
      .then((data) => {
        if (signal.aborted) return;
        setContent(data);
      })
      .catch((err) => {
        if (signal.aborted || err.name === 'AbortError') return;
        console.error(`[useSiteContent] Failed to fetch content for key "${key}":`, err);
        setError(err);
        setContent(latestFallbackRef.current);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, [key, normalizedKey, enabled]);

  useEffect(() => {
    if (!enabled || !normalizedKey) {
      setLoading(false);
      return;
    }
    const cached = getCached(normalizedKey);
    if (cached !== null) {
      setContent(cached);
      setLoading(false);
      return;
    }
    fetchContent();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [normalizedKey, enabled, fetchContent]);

  const refetch = useCallback(() => {
    if (normalizedKey) contentCache.delete(normalizedKey);
    fetchContent();
  }, [fetchContent, normalizedKey]);

  return { content, loading, error, refetch };
}

/**
 * Get image URL with cache-busting for site content
 * @param {object} content - Content object with image
 * @param {string} fallback - Fallback image path
 * @returns {string} Image URL
 */
export function getContentImageUrl(content, fallback = '/hero.png') {
  if (!content?.image) return fallback;
  // Handle updatedAt as timestamp or Date string
  const timestamp = content.updatedAt
    ? (typeof content.updatedAt === 'number'
      ? content.updatedAt
      : new Date(content.updatedAt).getTime())
    : null;
  return getImageUrl(content.image, timestamp, fallback);
}
